import React, { useState } from "react";
import { overrideFx, revertFx, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Coins, Lock, Unlock, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const FxRateCard = ({ inv, onChanged }) => {
  const { user } = useApp();
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState("rate");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!inv.base_currency || inv.currency === inv.base_currency) return null;
  const locked = inv.fx_locked;
  const preview = value && Number(value) > 0
    ? (mode === "rate" ? money(inv.total * Number(value), inv.base_currency) : `1 ${inv.currency} = ${(Number(value) / inv.total).toFixed(6)} ${inv.base_currency}`)
    : null;

  const save = async () => {
    if (!value || Number(value) <= 0) return toast.error("Enter a positive number");
    setBusy(true);
    try {
      const body = { user, note: note || null, [mode === "rate" ? "rate" : "base_total"]: Number(value) };
      const updated = await overrideFx(inv.id, body);
      onChanged(updated);
      toast.success(`Rate locked — ${money(updated.base_total, updated.base_currency)} at ${updated.fx_rate}`);
      setEditing(false); setValue(""); setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock rate");
    }
    setBusy(false);
  };

  const lockUsual = async () => {
    setBusy(true);
    try {
      const updated = await overrideFx(inv.id, { user, rate: inv.fx_alert.usual_rate, note: `Locked at supplier's usual rate (ECB ${inv.fx_rate} was ${Math.abs(inv.fx_alert.change_pct).toFixed(1)}% off)` });
      onChanged(updated);
      toast.success(`Locked at usual rate ${updated.fx_rate} — ${money(updated.base_total, updated.base_currency)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock rate");
    }
    setBusy(false);
  };

  const revert = async () => {
    setBusy(true);
    try {
      const updated = await revertFx(inv.id, user);
      onChanged(updated);
      toast.success(`Back to ECB rate ${updated.fx_rate}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="fx-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Coins className="h-4 w-4 text-sky-400" /> Exchange rate
          {locked ? (
            <span data-testid="fx-locked-badge" className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-500"><Lock className="h-3 w-3" /> Locked by {locked.by}</span>
          ) : (
            <span data-testid="fx-ecb-badge" className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">ECB {inv.fx_date}</span>
          )}
        </div>
        {!editing && (
          <Button data-testid="fx-edit-btn" size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> {locked ? "Change" : "Use bank rate"}
          </Button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Rate</div>
          <div className="font-mono font-medium" data-testid="fx-rate-value">{inv.fx_rate ? `1 ${inv.currency} = ${inv.fx_rate} ${inv.base_currency}` : "unavailable"}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Total in {inv.base_currency}</div>
          <div className="font-mono font-medium" data-testid="fx-base-total">{inv.base_total != null ? money(inv.base_total, inv.base_currency) : "—"}</div>
        </div>
      </div>
      {inv.fx_alert && (
        <div className={cn("mt-2 rounded-md border px-2.5 py-1.5 text-[11px]", inv.fx_alert.resolved ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400")} data-testid="fx-alert-note">
          {inv.fx_alert.resolved ? (
            <>Rate spike resolved — locked at {inv.fx_alert.resolved.rate} ({money(inv.fx_alert.resolved.base_total, inv.base_currency)}) by {inv.fx_alert.resolved.by}. ECB rate was {inv.fx_alert.rate}, {Math.abs(inv.fx_alert.change_pct).toFixed(1)}% off the supplier's usual {inv.fx_alert.usual_rate}.</>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-1 min-w-[12rem]">
                Rate moved {Math.abs(inv.fx_alert.change_pct).toFixed(1)}% {inv.fx_alert.change_pct > 0 ? "above" : "below"} this supplier's usual {inv.fx_alert.usual_rate} (last {inv.fx_alert.sample} invoices) —
                {" "}{money(Math.abs(inv.fx_alert.diff_base_total || 0), inv.base_currency)} {inv.fx_alert.diff_base_total > 0 ? "more" : "less"} in {inv.base_currency}.{inv.fx_alert.emailed ? " Team emailed." : ""}
              </span>
              {!locked && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500/40" disabled={busy} onClick={lockUsual} data-testid="fx-lock-usual-btn">
                  {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Lock className="h-3 w-3 mr-1" />} Lock at usual rate {inv.fx_alert.usual_rate}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {locked?.note && <p className="mt-2 text-xs text-muted-foreground" data-testid="fx-lock-note">Note: {locked.note}</p>}
      {locked?.previous?.rate && (
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="fx-previous">ECB rate was {locked.previous.rate} ({money(locked.previous.base_total, inv.base_currency)}) — base-currency changes and re-rating will not touch this invoice.</p>
      )}

      {editing && (
        <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid="fx-edit-form">
          <div className="flex rounded-lg border border-border p-0.5 text-xs w-fit">
            {[["rate", "Enter rate"], ["amount", `Enter ${inv.base_currency} amount`]].map(([k, l]) => (
              <button key={k} data-testid={`fx-mode-${k}`} onClick={() => { setMode(k); setValue(""); }}
                      className={`rounded-md px-3 py-1.5 font-medium ${mode === k ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input data-testid="fx-value-input" type="number" step="any" min="0" className="font-mono" value={value} onChange={(e) => setValue(e.target.value)}
                   placeholder={mode === "rate" ? `e.g. 0.7812 (${inv.base_currency} per 1 ${inv.currency})` : `exact ${inv.base_currency} amount from your bank statement`} />
          </div>
          {preview && <div className="text-xs text-muted-foreground font-mono" data-testid="fx-preview">→ {preview}</div>}
          <Input data-testid="fx-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (e.g. HSBC rate on statement 12 Jun)" className="text-sm" maxLength={200} />
          <div className="flex flex-wrap justify-end gap-2">
            {locked && (
              <Button data-testid="fx-revert-btn" size="sm" variant="ghost" onClick={revert} disabled={busy}>
                <Unlock className="h-3.5 w-3.5 mr-1.5" /> Revert to ECB rate
              </Button>
            )}
            <Button data-testid="fx-cancel-btn" size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="fx-save-btn" size="sm" onClick={save} disabled={busy || !value}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Lock className="h-3.5 w-3.5 mr-1.5" />} Lock rate
            </Button>
          </div>
        </div>
      )}
      {!editing && locked && (
        <div className="mt-3 flex justify-end">
          <Button data-testid="fx-revert-inline-btn" size="sm" variant="ghost" onClick={revert} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Unlock className="h-3.5 w-3.5 mr-1.5" />} Revert to ECB rate
          </Button>
        </div>
      )}
    </div>
  );
};
